/**
 * Explainable, rules-based evidence and challenge choice (Phase 7, ADR 0013). Knowledge evidence
 * comes only from finished challenge attempts; interests are never an input. Every status and
 * every suggestion carries a reason code and the attempts behind it, and is always provisional:
 * a first guess from a few puzzles, never a mark, a credential or a claim about the child.
 */

export const EVIDENCE_STATUSES = ['unobserved', 'emerging', 'practised', 'demonstrated'] as const;
export type EvidenceStatus = (typeof EVIDENCE_STATUSES)[number];

/** A challenge as the engine needs it (from the manifest, in authored order). */
export interface ChallengeInfo {
  id: string;
  objectiveId: string;
  /** 1 = entry level, 2 = harder. */
  level: number;
}

/** One finished attempt: solved, or left/restarted after at least one test. */
export interface AttemptRecord {
  challengeId: string;
  challengeVersion: number;
  objectiveId: string;
  level: number;
  outcome: 'solved' | 'not-yet';
  /** How many times "Test my circuit" was pressed. */
  tests: number;
  hintsUsed: number;
  /** The last hint highlights the next connection. */
  showMeUsed: boolean;
  /** ISO time the attempt ended. */
  endedAt: string;
}

/** "On your own": at most one hint, no "show me", at most three tests. */
export const INDEPENDENT_LIMITS = { maxHints: 1, maxTests: 3 } as const;

export function solvedIndependently(a: AttemptRecord): boolean {
  return (
    a.outcome === 'solved' &&
    !a.showMeUsed &&
    a.hintsUsed <= INDEPENDENT_LIMITS.maxHints &&
    a.tests <= INDEPENDENT_LIMITS.maxTests
  );
}

export type StatusReason =
  | 'no-attempts'
  | 'tried-not-solved'
  | 'solved-with-show-me'
  | 'solved-entry'
  | 'solved-harder-with-help'
  | 'solved-harder-independently';

export interface ObjectiveEvidence {
  objectiveId: string;
  status: EvidenceStatus;
  reason: StatusReason;
  /** The attempt the status rests on (null when unobserved). */
  keyAttempt: AttemptRecord | null;
  /** All attempts for this objective, oldest first. */
  attempts: readonly AttemptRecord[];
  /** Always true: a first guess from a handful of puzzles. */
  provisional: true;
}

export function objectiveEvidence(
  objectiveId: string,
  attempts: readonly AttemptRecord[],
): ObjectiveEvidence {
  const mine = attempts.filter((a) => a.objectiveId === objectiveId);
  const result = (
    status: EvidenceStatus,
    reason: StatusReason,
    keyAttempt: AttemptRecord | null,
  ): ObjectiveEvidence => ({
    objectiveId,
    status,
    reason,
    keyAttempt,
    attempts: mine,
    provisional: true,
  });
  const latest = (list: AttemptRecord[]) => list[list.length - 1] ?? null;

  if (mine.length === 0) return result('unobserved', 'no-attempts', null);
  const harderOwn = mine.filter((a) => a.level >= 2 && solvedIndependently(a));
  if (harderOwn.length > 0)
    return result('demonstrated', 'solved-harder-independently', latest(harderOwn));
  const harder = mine.filter((a) => a.level >= 2 && a.outcome === 'solved');
  if (harder.length > 0) return result('practised', 'solved-harder-with-help', latest(harder));
  const entry = mine.filter((a) => a.outcome === 'solved' && !a.showMeUsed);
  if (entry.length > 0) return result('practised', 'solved-entry', latest(entry));
  const shown = mine.filter((a) => a.outcome === 'solved');
  if (shown.length > 0) return result('emerging', 'solved-with-show-me', latest(shown));
  return result('emerging', 'tried-not-solved', latest(mine));
}

/** Objectives in the order the challenges introduce them. */
export function objectiveOrder(challenges: readonly ChallengeInfo[]): string[] {
  return [...new Set(challenges.map((c) => c.objectiveId))];
}

export function summarise(
  challenges: readonly ChallengeInfo[],
  attempts: readonly AttemptRecord[],
): ObjectiveEvidence[] {
  return objectiveOrder(challenges).map((id) => objectiveEvidence(id, attempts));
}

export type StartChoice = 'beginning' | 'harder';

export type RecommendationReason =
  /** Nothing played yet: the entry task. */
  | 'start'
  /** The child said they have done circuits before (a choice, not evidence). */
  | 'start-harder'
  /** Solved the entry level on their own: the harder level of the same idea. */
  | 'step-up'
  /** Moving on to the next idea at its entry level. */
  | 'next-idea'
  /** Two harder challenges in a row on their own: the next idea starts at the harder level. */
  | 'fast-track'
  /** Not solved at the harder level: the entry level of the same idea. */
  | 'step-down'
  /** Not solved yet: the same challenge again is fine. */
  | 'try-again'
  /** Back to an idea that needed help earlier, one step up. */
  | 'revisit'
  /** Every idea has been shown: free choice. */
  | 'all-shown';

export interface Recommendation {
  challengeId: string | null;
  reason: RecommendationReason;
  /** The attempt the suggestion reacts to (null at the start). */
  because: AttemptRecord | null;
}

function pick(
  challenges: readonly ChallengeInfo[],
  objectiveId: string,
  level: number,
): ChallengeInfo | undefined {
  const same = challenges.filter((c) => c.objectiveId === objectiveId);
  return same.find((c) => c.level === level) ?? same.find((c) => c.level === 1) ?? same[0];
}

/**
 * The short adaptive rule (spec feature 6): from the latest finished attempt and the evidence so
 * far, suggest one next challenge and say why. Harder levels come only after solving on one's
 * own; easier ones after not solving a harder one. The child can always choose differently.
 */
export function recommend(
  challenges: readonly ChallengeInfo[],
  attempts: readonly AttemptRecord[],
  start: StartChoice = 'beginning',
): Recommendation {
  const order = objectiveOrder(challenges);
  const first = order[0];
  if (!first) return { challengeId: null, reason: 'all-shown', because: null };
  const last = attempts[attempts.length - 1];
  if (!last) {
    const level = start === 'harder' ? 2 : 1;
    return {
      challengeId: pick(challenges, first, level)?.id ?? null,
      reason: start === 'harder' ? 'start-harder' : 'start',
      because: null,
    };
  }

  const status = (id: string) => objectiveEvidence(id, attempts).status;
  const suggest = (c: ChallengeInfo | undefined, reason: RecommendationReason): Recommendation => ({
    challengeId: c?.id ?? null,
    reason: c ? reason : 'all-shown',
    because: last,
  });

  if (last.outcome === 'not-yet') {
    if (last.level >= 2) return suggest(pick(challenges, last.objectiveId, 1), 'step-down');
    return suggest(
      challenges.find((c) => c.id === last.challengeId),
      'try-again',
    );
  }

  if (
    last.level === 1 &&
    solvedIndependently(last) &&
    status(last.objectiveId) !== 'demonstrated'
  ) {
    const harder = challenges.find((c) => c.objectiveId === last.objectiveId && c.level >= 2);
    if (harder) return suggest(harder, 'step-up');
  }

  // Move on: the next idea after this one that is not shown yet (wrapping round to earlier ones).
  const at = order.indexOf(last.objectiveId);
  const rotated = [...order.slice(at + 1), ...order.slice(0, at + 1)];
  const nextId = rotated.find((id) => status(id) !== 'demonstrated');
  if (!nextId) return { challengeId: null, reason: 'all-shown', because: last };

  const nextStatus = status(nextId);
  if (nextStatus === 'practised') return suggest(pick(challenges, nextId, 2), 'revisit');
  if (nextStatus === 'emerging') return suggest(pick(challenges, nextId, 1), 'revisit');
  const previous = attempts[attempts.length - 2];
  const twoOnOwn =
    last.level >= 2 &&
    solvedIndependently(last) &&
    !!previous &&
    previous.level >= 2 &&
    solvedIndependently(previous);
  return twoOnOwn
    ? suggest(pick(challenges, nextId, 2), 'fast-track')
    : suggest(pick(challenges, nextId, 1), 'next-idea');
}
